import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import { useAuth } from "context/authContext";
import {
  getEmployeesPaging,
  getJobs,
  getActiveSalaryComponents,
  getInputTemplates,
  getPayrollInputsForEmployeePeriod,
  addPayrollInput,
  generateInputsFromTemplate,
  updatePayrollInput,
  submitPayrollInput,
  approvePayrollInput,
  deletePayrollInput,
} from "services/api";

const inputStatusColors = {
  draft: "default",
  submitted: "info",
  approved: "success",
};

// Backend only accepts new inputs (manual or generated-from-template) while
// the period is "open" or "processing" - "closed"/"locked" is read-only.
const periodAcceptsNewInputs = (period) =>
  period?.status === "open" || period?.status === "processing";

const InputGridView = ({ canEdit, selectedPeriod }) => {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const snackbarRef = useRef();

  const [jobs, setJobs] = useState([]);
  const [components, setComponents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingStatic, setLoadingStatic] = useState(true);

  const [pagedEmployees, setPagedEmployees] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(false);

  const [jobFilter, setJobFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [inputs, setInputs] = useState([]);
  const [loadingInputs, setLoadingInputs] = useState(false);
  const [editingAmounts, setEditingAmounts] = useState({}); // { [inputId]: amountString }

  const [generateOpen, setGenerateOpen] = useState(false);
  const [templateChoice, setTemplateChoice] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newComponent, setNewComponent] = useState(null);
  const [newAmount, setNewAmount] = useState("");

  const [pendingDelete, setPendingDelete] = useState(null);

  const canWrite = canEdit && periodAcceptsNewInputs(selectedPeriod);

  useEffect(() => {
    const loadStatic = async () => {
      setLoadingStatic(true);
      const [jobsRes, componentsRes, templatesRes] = await Promise.all([
        getJobs(),
        getActiveSalaryComponents(),
        getInputTemplates(),
      ]);
      if (jobsRes !== "error") setJobs(jobsRes);
      if (componentsRes !== "error") setComponents(componentsRes);
      if (templatesRes !== "error") setTemplates(templatesRes);
      setLoadingStatic(false);
    };
    loadStatic();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [jobFilter, debouncedSearch]);

  const fetchPage = useCallback(async () => {
    setTableLoading(true);
    const res = await getEmployeesPaging(
      page,
      rowsPerPage,
      jobFilter?.jobId || undefined,
      undefined,
      undefined,
      debouncedSearch || undefined
    );
    if (res === "error") {
      setPagedEmployees([]);
      setTotalElements(0);
    } else {
      const content = Array.isArray(res) ? res : res?.content || [];
      const total = Array.isArray(res) ? res.length : (res?.totalElements ?? content.length);
      setPagedEmployees(content);
      setTotalElements(total);
    }
    setTableLoading(false);
  }, [page, rowsPerPage, jobFilter, debouncedSearch]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const componentName = (id) => components.find((c) => c.id === id)?.name || `#${id}`;

  const templatesForEmployee = selectedEmployee
    ? templates.filter((tpl) => tpl.jobId === selectedEmployee.jobId)
    : [];

  const loadInputs = useCallback(
    async (emp) => {
      if (!emp || !selectedPeriod) return;
      setLoadingInputs(true);
      const res = await getPayrollInputsForEmployeePeriod(selectedPeriod.id, emp.empId);
      setInputs(res !== "error" && Array.isArray(res) ? res : []);
      setEditingAmounts({});
      setLoadingInputs(false);
    },
    [selectedPeriod?.id]
  );

  const handleSelectEmployee = async (emp) => {
    setSelectedEmployee(emp);
    await loadInputs(emp);
  };

  const handleGenerate = async () => {
    if (!templateChoice) return;
    const res = await generateInputsFromTemplate(
      selectedEmployee.empId,
      selectedPeriod.id,
      templateChoice.id
    );
    if (res !== "error") {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.generateSuccess"),
        "success"
      );
      setGenerateOpen(false);
      setTemplateChoice(null);
      loadInputs(selectedEmployee);
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.generateError"),
        "error"
      );
    }
  };

  const handleAdd = async () => {
    if (!newComponent || newAmount === "") return;
    const res = await addPayrollInput({
      employeeId: selectedEmployee.empId,
      payrollPeriodId: selectedPeriod.id,
      salaryComponentId: newComponent.id,
      amount: Number(newAmount),
      source: "manual",
    });
    if (res !== "error") {
      setInputs([...inputs, res]);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.addSuccess"),
        "success"
      );
      setAddOpen(false);
      setNewComponent(null);
      setNewAmount("");
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.addError"),
        "error"
      );
    }
  };

  const handleSaveAmount = async (input) => {
    const amount = Number(editingAmounts[input.id]);
    if (Number.isNaN(amount)) return;
    const res = await updatePayrollInput(input.id, { ...input, amount });
    if (res !== "error") {
      setInputs(inputs.map((i) => (i.id === res.id ? res : i)));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.updateSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.updateError"),
        "error"
      );
    }
  };

  const handleSubmitInput = async (input) => {
    const res = await submitPayrollInput(input.id);
    if (res !== "error") {
      setInputs(inputs.map((i) => (i.id === res.id ? res : i)));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.submitSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.submitError"),
        "error"
      );
    }
  };

  const handleApproveInput = async (input) => {
    const res = await approvePayrollInput(input.id, employee?.id);
    if (res !== "error") {
      setInputs(inputs.map((i) => (i.id === res.id ? res : i)));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.approveSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.approveError"),
        "error"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const res = await deletePayrollInput(pendingDelete.id);
    if (res === "success") {
      setInputs(inputs.filter((i) => i.id !== pendingDelete.id));
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.deleteSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.inputGrid.deleteError"),
        "error"
      );
    }
    setPendingDelete(null);
  };

  if (loadingStatic) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      {!periodAcceptsNewInputs(selectedPeriod) && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t("admin.payroll.periodsAndRuns.inputGrid.readOnlyHelper", {
            status: selectedPeriod?.status,
          })}
          sx={{ mb: 2 }}
        />
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(job) => job.jobName || ""}
              isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
              value={jobFilter}
              onChange={(event, newValue) => setJobFilter(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.payroll.employees.filterByJob")} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              label={t("admin.payroll.employees.searchEmployee")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={selectedEmployee ? 5 : 12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {tableLoading ? (
              <LoadingScreen />
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.employee")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.job")}
                        </TableCell>
                      </TableRow>
                      {pagedEmployees.map((emp) => (
                        <TableRow
                          key={emp.empId}
                          hover
                          selected={selectedEmployee?.empId === emp.empId}
                          onClick={() => handleSelectEmployee(emp)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{emp.empName}</TableCell>
                          <TableCell>{emp.jobName || t("admin.payroll.employees.noJob")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalElements}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </Paper>
        </Grid>

        {selectedEmployee && (
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <MDBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <MDBox>
                  <MDTypography variant="subtitle2">{selectedEmployee.empName}</MDTypography>
                  <MDTypography variant="caption" color="text">
                    {selectedEmployee.jobName || t("admin.payroll.employees.noJob")}
                  </MDTypography>
                </MDBox>
                {canWrite && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => setGenerateOpen(true)}
                      disabled={templatesForEmployee.length === 0}
                    >
                      {t("admin.payroll.periodsAndRuns.inputGrid.generateFromTemplate")}
                    </MDButton>
                    <MDButton size="small" color="primary" onClick={() => setAddOpen(true)}>
                      {t("admin.payroll.periodsAndRuns.inputGrid.addManual")}
                    </MDButton>
                  </Box>
                )}
              </MDBox>

              {loadingInputs ? (
                <LoadingScreen />
              ) : inputs.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  {t("admin.payroll.periodsAndRuns.inputGrid.noInputs")}
                </MDTypography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.periodsAndRuns.inputGrid.fields.component")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.periodsAndRuns.inputGrid.fields.amount")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                          {t("admin.payroll.periodsAndRuns.inputGrid.fields.status")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                          {t("admin.payroll.periodsAndRuns.inputGrid.fields.actions")}
                        </TableCell>
                      </TableRow>
                      {inputs.map((input) => {
                        const isDraft = input.status === "draft";
                        const editValue = editingAmounts[input.id] ?? String(input.amount);
                        const isDirty = isDraft && editValue !== String(input.amount);
                        return (
                          <TableRow key={input.id} hover>
                            <TableCell>{componentName(input.salaryComponentId)}</TableCell>
                            <TableCell>
                              {isDraft && canEdit ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editValue}
                                  onChange={(e) =>
                                    setEditingAmounts((prev) => ({
                                      ...prev,
                                      [input.id]: e.target.value,
                                    }))
                                  }
                                  sx={{ width: 110 }}
                                />
                              ) : (
                                input.amount
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={t(
                                  `admin.payroll.periodsAndRuns.inputGrid.statuses.${input.status}`
                                )}
                                color={inputStatusColors[input.status] || "default"}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  justifyContent: "flex-end",
                                  flexWrap: "wrap",
                                }}
                              >
                                {canEdit && isDraft && isDirty && (
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleSaveAmount(input)}
                                  >
                                    {t("common.save")}
                                  </MDButton>
                                )}
                                {canEdit && isDraft && (
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleSubmitInput(input)}
                                  >
                                    {t("admin.payroll.periodsAndRuns.inputGrid.submit")}
                                  </MDButton>
                                )}
                                {canEdit && input.status === "submitted" && (
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleApproveInput(input)}
                                  >
                                    {t("admin.payroll.periodsAndRuns.inputGrid.approve")}
                                  </MDButton>
                                )}
                                {canEdit && isDraft && (
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setPendingDelete(input)}
                                  >
                                    {t("common.delete")}
                                  </MDButton>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("admin.payroll.periodsAndRuns.inputGrid.generateFromTemplate")}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={templatesForEmployee}
            getOptionLabel={(tpl) => tpl.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={templateChoice}
            onChange={(event, newValue) => setTemplateChoice(newValue)}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.payroll.periodsAndRuns.inputGrid.template")} />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setGenerateOpen(false)} color="secondary">
            {t("common.cancel")}
          </MDButton>
          <MDButton onClick={handleGenerate} color="primary" disabled={!templateChoice}>
            {t("admin.payroll.periodsAndRuns.inputGrid.generate")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("admin.payroll.periodsAndRuns.inputGrid.addManual")}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1, mb: 2 }}
            options={components}
            getOptionLabel={(c) => c.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={newComponent}
            onChange={(event, newValue) => setNewComponent(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.payroll.periodsAndRuns.inputGrid.fields.component")}
              />
            )}
          />
          <TextField
            label={t("admin.payroll.periodsAndRuns.inputGrid.fields.amount")}
            type="number"
            value={newAmount}
            onChange={(e) => setNewAmount(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setAddOpen(false)} color="secondary">
            {t("common.cancel")}
          </MDButton>
          <MDButton
            onClick={handleAdd}
            color="primary"
            disabled={!newComponent || newAmount === ""}
          >
            {t("common.add")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        message={t("admin.payroll.periodsAndRuns.inputGrid.confirmDelete")}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

InputGridView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  selectedPeriod: PropTypes.object,
};

InputGridView.defaultProps = {
  selectedPeriod: null,
};

export default InputGridView;
